import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { type AyotResponse } from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

const { coachingAnalytics } = appStrings.modules;

export const useAgentContext = (data: AyotResponse, detail = false) => {
  useRegisterAgentContext({
    kind: 'section',
    title: coachingAnalytics.onTrack.title,
    description: coachingAnalytics.onTrack.subtitle,
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: coachingAnalytics.onTrack.aspiration.title,
    display:
      'Two cards side by side: a donut of the ahead/aligned/behind split, and a heat matrix of student counts.',
    detail: [
      coachingAnalytics.onTrack.aspiration.matrixInfo,
      coachingAnalytics.onTrack.aspiration.caveat,
      data.aspiration_vs_reality.excluded.no_blueprint > 0 &&
        coachingAnalytics.onTrack.excluded(data.aspiration_vs_reality.excluded.no_blueprint),
    ]
      .filter(Boolean)
      .join('\n\n'),
    data: {
      assessed: data.assessed,
      tracks: data.tracks.map((track) => track.name),
      total: data.aspiration_vs_reality.total,
      statuses: data.aspiration_vs_reality.statuses,
      matrix: data.aspiration_vs_reality.matrix,
      excluded: data.aspiration_vs_reality.excluded,
    },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: coachingAnalytics.onTrack.academicProfile.title,
    display:
      'Side-by-side histograms of the Academic Profile — grades, testing where available, and rigor — split between high school and post-secondary, with each band also split by the track those students can reach.',
    detail: [
      coachingAnalytics.onTrack.academicProfile.description,
      data.academic_profile.unclassified > 0 &&
        coachingAnalytics.onTrack.academicProfile.unclassified(data.academic_profile.unclassified),
    ]
      .filter(Boolean)
      .join('\n\n'),
    data: data.academic_profile,
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: coachingAnalytics.onTrack.onTrackStatus.title,
    display:
      'Donut of the on-track/developing/not-yet non-academic verdict, with one stacked bar per category.',
    detail: [
      coachingAnalytics.onTrack.statusByUnlock.nonAcademicCategories,
      data.non_academic_profile.excluded.no_blueprint > 0 &&
        coachingAnalytics.onTrack.excluded(data.non_academic_profile.excluded.no_blueprint),
    ]
      .filter(Boolean)
      .join('\n\n'),
    data: data.non_academic_profile,
  });

  useRegisterAgentContext(
    detail
      ? {
          kind: 'visual',
          label: coachingAnalytics.onTrack.heatMap.title,
          display:
            'Four category tabs and a grade dropdown; one row per rubric dimension, one shaded cell per level (Not yet / Proficient / Advanced), or per career track for GPA and ACT/SAT, or per band for Coursework Rigor, Major Rigor and College Selectivity, showing the share of students whose latest scored assessment landed there. In Academic Foundations the level-scored measures (Post-secondary Admission, Academic Skills, Applied Academic Skills, Capstone/Final Work) are drawn last as one stacked bar each instead of heat cells. The unit is students; Semi-Skilled students are excluded.',
          detail: [
            coachingAnalytics.onTrack.heatMap.info.rows,
            coachingAnalytics.onTrack.heatMap.info.grades,
            data.heat_map.excluded.semi_skilled > 0 &&
              coachingAnalytics.onTrack.heatMap.info.semiSkilled(
                data.heat_map.excluded.semi_skilled,
              ),
            data.heat_map.no_grade > 0 &&
              coachingAnalytics.onTrack.heatMap.info.noGrade(data.heat_map.no_grade),
          ]
            .filter(Boolean)
            .join('\n\n'),
          data: {
            unit: 'students',
            total: data.heat_map.total,
            excluded: data.heat_map.excluded,
            grades: data.heat_map.grades,
            categories: data.heat_map.categories.map((category) => ({
              name: category.name,
              dimensions: category.dimensions.map((dimension) => ({
                id: dimension.id,
                name: dimension.name,
                kind: dimension.kind,
                counts: dimension.counts,
              })),
            })),
          },
        }
      : null,
  );
};
